import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

export const FIND_LO_PATH = '/find-a-loan-officer';
export const FIND_LO_STATES_PATH = '/find-a-loan-officer?tab=states';

const GOLD_BTN = { bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5 };

/**
 * Shared two-step "Apply Now" modal flow.
 *
 * Step 1: "Are you currently working with one of our Loan Officers?"
 *   YES -> /find-a-loan-officer (Loan Officers list)
 *   NO  -> Step 2
 * Step 2: "No problem! Let's connect you with the right person today."
 *   GET STARTED -> /find-a-loan-officer?tab=states (States list)
 *
 * Controlled by the parent via `open` / `onClose`. The step resets to the
 * first question every time the flow is opened.
 */
export default function ApplyFlowDialogs({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('ask');

  useEffect(() => {
    if (open) setStep('ask');
  }, [open]);

  const handleYes = () => {
    onClose();
    navigate(FIND_LO_PATH);
  };

  const handleNo = () => setStep('connect');

  const handleGetStarted = () => {
    onClose();
    navigate(FIND_LO_STATES_PATH);
  };

  const paperProps = { sx: { borderRadius: 2, p: { xs: 2, md: 3 } } };
  const closeBtnSx = { position: 'absolute', right: 8, top: 8, color: 'text.secondary' };

  return (
    <>
      {/* Step 1 */}
      <Dialog open={open && step === 'ask'} onClose={onClose} fullWidth maxWidth="sm" PaperProps={paperProps}>
        <IconButton onClick={onClose} sx={closeBtnSx} aria-label="Close">
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={3} textAlign="center">
            <Typography variant="h3" component="h2">
              Are you currently working with one of our Loan Officers?
            </Typography>
            <Typography color="text.secondary">
              This will save you time by connecting you with the right person.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" onClick={handleYes} sx={GOLD_BTN}>
                YES
              </Button>
              <Button variant="contained" onClick={handleNo} sx={GOLD_BTN}>
                NO
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Step 2 */}
      <Dialog open={open && step === 'connect'} onClose={onClose} fullWidth maxWidth="sm" PaperProps={paperProps}>
        <IconButton onClick={onClose} sx={closeBtnSx} aria-label="Close">
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={3} textAlign="center">
            <Typography variant="h3" component="h2">
              No problem!
            </Typography>
            <Typography variant="body1">
              Let&rsquo;s connect you with the right person today.
            </Typography>
            <Button variant="contained" onClick={handleGetStarted} sx={{ ...GOLD_BTN, alignSelf: 'center' }}>
              GET STARTED
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
